import React, { useEffect, useMemo, useRef, useState } from "react";
import { styled, useTheme } from "@mui/material/styles";
import {
  AppBar,
  Toolbar,
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Divider,
  CssBaseline,
  IconButton,
  useMediaQuery,
  Paper,
  Typography,
  Button,
  Avatar,
  MenuItem,
  Fade,
  Menu,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  CircularProgress,
} from "@mui/material";
import {
  Home as HomeIcon,
  ListAlt as ListAltIcon,
  BarChart as BarChartIcon,
  Book as BookIcon,
  AutoGraph as AutoGraphIcon,
  Equalizer as EqualizerIcon,
  MilitaryTech as MilitaryTechIcon,
  Person as PersonIcon,
  Menu as MenuIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
  Logout as LogoutIcon,
  Subscriptions as SubscriptionsIcon,
  Group as GroupIcon,
  CalendarMonth as CalendarMonthIcon,
  Close,
  Summarize as SummarizeIcon,
  LibraryBooks as LibraryBooksIcon,
  ContactMail as ContactMailIcon,
} from "@mui/icons-material";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import CompanyLogo from "../images/svg/company-logo.svg";
import { logout } from "../features/auth-module/authSlice";
import { AppDispatch, RootState } from "../stores";
import { useDispatch, useSelector } from "react-redux";
import {
  getBillingDetailsByUserId,
  getSubscriptionByUserId,
  getSubscriptionDetailsAction,
  getUserInvoicesListAction,
} from "../features/subscription-module/subscriptionAction";
import { getAuthenticatedUser } from "../features/auth-module/authAction";
import PerfectScrollbar from "react-perfect-scrollbar";
import "react-perfect-scrollbar/dist/css/styles.css";
import { loadStripe } from "@stripe/stripe-js";
import { Elements } from "@stripe/react-stripe-js";

// Load Stripe with public key
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY!);

interface LayoutProps {
  children?: React.ReactNode;
}

const drawerWidth = 220;

// Styled Components (unchanged)
const Main = styled("main", {
  shouldForwardProp: (prop) => prop !== "open",
})<{ open?: boolean }>(({ theme, open }) => ({
  flexGrow: 1,
  transition: theme.transitions.create("margin", {
    easing: theme.transitions.easing.easeOut,
    duration: theme.transitions.duration.standard,
  }),
  marginLeft: open ? drawerWidth : 0,
  minHeight: "100vh",
  width: "100%",
  background: theme.palette.background.default,
  [theme.breakpoints.down("md")]: {
    marginLeft: 0,
    padding: theme.spacing(2),
  },
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(1.5),
  },
  [theme.breakpoints.down(400)]: {
    padding: theme.spacing(1),
  },
}));

const DrawerStyled = styled(Drawer)(({ theme }) => ({
  "& .MuiDrawer-paper": {
    width: drawerWidth,
    boxSizing: "border-box",
    zIndex: theme.zIndex.drawer,
    borderRight: "none",
    color: "#dbdede",
    background: "linear-gradient(to top, #758d8e, #4b5b5d)",
    transition: theme.transitions.create(["transform", "width"], {
      easing: theme.transitions.easing.easeInOut,
      duration: theme.transitions.duration.standard,
    }),
    [theme.breakpoints.down("md")]: {
      width: "50vw",
      maxWidth: 320,
    },
    [theme.breakpoints.down("sm")]: {
      width: "70vw",
      maxWidth: 280,
    },
    [theme.breakpoints.down(400)]: {
      width: "85vw",
      maxWidth: 240,
    },
  },
}));

const NavLinkStyled = styled(NavLink)(({ theme }) => ({
  textDecoration: "none",
  color: "#dbdede",
  borderRadius: theme.shape.borderRadius,
  width: "90vw",
  transition: "background 0.2s ease, transform 0.2s ease",
  "&.active": {
    border: `1px solid #dbdede`,
    color: theme.palette.primary.contrastText,
    "& .MuiListItemIcon-root": {
      color: theme.palette.primary.contrastText,
    },
    "& .MuiListItemText-primary": {
      fontWeight: 600,
    },
  },
  "&:hover": {
    background: "#dbdede",
    color: theme.palette.text.primary,
    transform: "translateX(4px)",
    "& .MuiListItemText-primary": {
      fontWeight: 600,
    },
    "& .MuiListItemIcon-root": {
      color: theme.palette.text.primary,
    },
  },
  "&:focus-visible": {
    outline: `2px solid ${theme.palette.primary.main}`,
    outlineOffset: 2,
  },
}));

const ContentContainer = styled(Paper)(({ theme }) => ({
  minHeight: "100vh",
  width: "100%",
  maxWidth: "auto",
  padding: theme.spacing(2),
  borderRadius: theme.spacing(0),
  background: "linear-gradient(to top, #758d8e, #4b5b5d)",
  [theme.breakpoints.down("md")]: {
    background: theme.palette.background.paper,
    padding: theme.spacing(1.5),
  },
  [theme.breakpoints.down("sm")]: {
    background: theme.palette.background.paper,
    padding: theme.spacing(0),
  },
  [theme.breakpoints.down(400)]: {
    background: theme.palette.background.paper,
    padding: theme.spacing(0),
  },
}));

const Content = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3.5),
  background: "#ffffff",
  borderRadius: theme.spacing(3),
  transition: "all 0.3s ease",
  position: "relative",
  overflow: "hidden",
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(1),
    borderRadius: theme.spacing(1),
  },
  [theme.breakpoints.down(400)]: {
    padding: theme.spacing(0.5),
    borderRadius: theme.spacing(0.5),
  },
}));

const AppBarStyled = styled(AppBar)(({ theme }) => ({
  background: "#acb4bc",
  boxShadow: theme.shadows[2],
  [theme.breakpoints.up("md")]: {
    display: "none",
  },
}));

const LogoContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  padding: theme.spacing(2, 2, 0, 7),
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(1.5),
  },
  [theme.breakpoints.down(400)]: {
    padding: theme.spacing(1),
  },
}));

const GradientDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialog-paper": {
    background: "linear-gradient(135deg, #758d8e 0%, #4b5b5d 100%)",
    borderRadius: "16px",
    boxShadow: "0 10px 30px rgba(0, 0, 0, 0.2)",
    border: "1px solid rgba(255, 255, 255, 0.1)",
    overflow: "hidden",
    maxWidth: "350px",
    width: "90%",
  },
}));

const DialogTitleStyled = styled(DialogTitle)(({ theme }) => ({
  display: "flex",
  flexDirection: "row",
  justifyContent: "center",
  background: "rgba(0, 0, 0, 0.1)",
  color: "#ffffff",
  fontWeight: 600,
  textAlign: "center",
  padding: theme.spacing(1),
  fontSize: "1rem",
  marginBottom: "12px",
}));

const DialogContentStyled = styled(DialogContent)(({ theme }) => ({
  padding: "20px",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  "& .MuiDialogContentText-root": {
    color: "#dbdede",
    textAlign: "center",
    fontSize: "1rem",
    marginBottom: "16px",
  },
}));

const DialogActionsStyled = styled(DialogActions)(({ theme }) => ({
  padding: "2px 20px 20px",
  justifyContent: "center",
  gap: "12px",
}));

const StyledDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialog-paper": {
    backgroundColor: "#fff",
    borderRadius: 12,
    maxWidth: 600,
    width: "100%",
    boxShadow: "0 4px 20px rgba(0,0,0,0.12)",
    [theme.breakpoints.down("sm")]: {
      margin: theme.spacing(1),
      maxWidth: "95%",
    },
  },
}));

const DialogHeader = styled(Box)(({ theme }) => ({
  background: "linear-gradient(to bottom, #758d8e, #4b5b5d)",
  padding: theme.spacing(2),
  color: "#f2efe9",
  borderTopLeftRadius: 12,
  borderTopRightRadius: 12,
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(1.5),
  },
}));

const DialogBody = styled(DialogContent)(({ theme }) => ({
  backgroundColor: "#fff",
  padding: theme.spacing(3),
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(2),
  },
}));

const DialogFooter = styled(DialogActions)(({ theme }) => ({
  backgroundColor: "#f2efe9",
  padding: theme.spacing(2),
  justifyContent: "center",
  borderBottomLeftRadius: 12,
  borderBottomRightRadius: 12,
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(1.5),
    flexDirection: "column",
    gap: theme.spacing(1),
  },
}));

const StyledButton = styled(Button)(({ theme }) => ({
  textTransform: "none",
  fontWeight: 600,
  backgroundColor: "#4b5b5d",
  color: "#fff",
  borderRadius: 8,
  padding: theme.spacing(1, 3),
  fontSize: 13,
  boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
  "&:hover": {
    backgroundColor: "#354142",
  },
  "&.Mui-disabled": {
    backgroundColor: "#bdbdbd",
    color: "#fff",
  },
  [theme.breakpoints.down("sm")]: {
    fontSize: 14,
    padding: theme.spacing(1, 2),
  },
}));

const CancelButton = styled(Button)(({ theme }) => ({
  textTransform: "none",
  fontWeight: 600,
  backgroundColor: "transparent",
  color: "#455354",
  borderRadius: 8,
  padding: theme.spacing(1, 3),
  fontSize: 13,
  "&:hover": {
    backgroundColor: "rgba(69, 83, 84, 0.1)",
  },
  [theme.breakpoints.down("sm")]: {
    fontSize: 14,
    padding: theme.spacing(1, 2),
  },
}));

const GradientButton = styled(Button)(({ theme }) => ({
  borderRadius: "8px",
  fontWeight: 600,
  padding: "5px 24px",
  minWidth: "90px",
  transition: "all 0.1s ease",
  "&.cancel-button": {
    background: "rgba(255, 255, 255, 0.1)",
    color: "#dbdede",
    border: "1px solid rgba(255, 255, 255, 0.2)",
    "&:hover": {
      background: "rgba(255, 255, 255, 0.2)",
    },
  },
  "&.logout-button": {
    background: "linear-gradient(45deg, #ff5252 0%, #ff7675 100%)",
    color: "white",
    boxShadow: "0 4px 10px rgba(255, 82, 82, 0.3)",
    "&:hover": {
      boxShadow: "0 6px 14px rgba(255, 82, 82, 0.4)",
      background: "linear-gradient(45deg, #ff5252 0%, #ff7675 100%)",
    },
  },
}));

const DrawerContentContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  minHeight: "93vh",
}));

const Layout: React.FC<LayoutProps> = () => {
  const theme = useTheme();
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const isVerySmallScreen = useMediaQuery(theme.breakpoints.down(400));
  const [open, setOpen] = React.useState<boolean>(!isMobile);

  const userData = JSON.parse(localStorage.getItem("user") || "{}");
  const userFirstname = userData?.firstname || "User";
  const userLastname = userData?.lastname || "";
  const userRole = userData?.role || "customer";
  const profileImg = userData?.profileImg || "";
  const avatarInitial = userFirstname.charAt(0) || "";
  const subscription = userData?.subscription || null;
  const isEmbedded = subscription?.isEmbedded || false;
  const isWhiteList = subscription?.isWhiteList || false;
  const isFleetEstimator = subscription?.isFleetEstimator || false;
  const isSiteEstimator = subscription?.isSiteEstimator || false;

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);
  const menuOpen = Boolean(anchorEl);
  const [upgradeAnchorEl, setUpgradeAnchorEl] = useState<null | HTMLElement>(
    null
  );
  const upgradeMenuOpen = Boolean(upgradeAnchorEl);
  const [upgradeDialogOpen, setUpgradeDialogOpen] = useState(false);
  const [selectedUpgradeDuration, setSelectedUpgradeDuration] = useState<
    "monthly" | "yearly"
  >("monthly");

  const { updateSubscriptionLoading } = useSelector(
    (state: RootState) => state.subscription
  );
  const { plans } = useSelector((state: RootState) => state.planManagement);

  useEffect(() => {
    setOpen(!isMobile);
  }, [isMobile]);

  const scrollContainerRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    requestAnimationFrame(() => {
      if (scrollContainerRef.current) {
        scrollContainerRef.current.scrollTop = 0;
        scrollContainerRef.current.scrollLeft = 0;
      }
      const scrollingElement =
        document.scrollingElement || document.documentElement;
      scrollingElement.scrollTo({ top: 0, left: 0, behavior: "auto" });
      document.body.scrollTop = 0;
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    });
  }, [location.pathname]);

  const handleDrawerToggle = () => {
    setOpen(!open);
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogoutClick = () => {
    handleMenuClose();
    setLogoutDialogOpen(true);
  };

  const handleLogoutConfirm = () => {
    dispatch(logout());
    setLogoutDialogOpen(false);
    navigate("/login");
  };

  const handleLogoutCancel = () => {
    setLogoutDialogOpen(false);
  };

  const currentPlanName = (
    userData?.subscription?.planName || ""
  ).toLowerCase();
  console.log("Current Plan Name:", currentPlanName);
  const getNextTier = (planName: string) => {
    if (planName === "core") return "growth";
    if (planName === "growth") return "dcfc starter";
    if (planName === "dcfc starter") return "dcfc pro";
    if (planName === "dcfc pro") return "dcfc pro";
    return "professional";
  };
  const nextTier = getNextTier(currentPlanName);

  const handleOpenUpgradeMenu = (event: React.MouseEvent<HTMLElement>) => {
    if (nextTier === "dcfc pro" && currentPlanName === "dcfc pro") return;
    setUpgradeAnchorEl(event.currentTarget);
  };

  const handleCloseUpgradeMenu = () => {
    setUpgradeAnchorEl(null);
  };

  const handleSelectDurationAndOpenDialog = (
    duration: "monthly" | "yearly"
  ) => {
    setSelectedUpgradeDuration(duration);
    handleCloseUpgradeMenu();
    navigate("/main/plans");
  };

  const handleCloseUpgradeDialog = async () => {
    setUpgradeDialogOpen(false);
    await Promise.all([
      dispatch(getSubscriptionByUserId() as any),
      dispatch(getSubscriptionDetailsAction() as any),
      dispatch(getBillingDetailsByUserId() as any),
      dispatch(getUserInvoicesListAction() as any),
      dispatch(getAuthenticatedUser() as any),
    ]);
  };

  useEffect(() => {
    if (upgradeDialogOpen) {
      // dispatch(getAllPlans({ mode: "" }) as any);
      // dispatch(getSubscriptionByUserId() as any);
      // dispatch(getBillingDetailsByUserId() as any);
    }
  }, [upgradeDialogOpen, dispatch]);

  const targetPlan = useMemo(() => {
    const tier = nextTier?.toLowerCase();
    return plans.find(
      (p) =>
        p.planName?.toLowerCase() === tier &&
        p.duration === selectedUpgradeDuration
    );
  }, [plans, nextTier, selectedUpgradeDuration]);

  const allNavItems = [
    {
      text: "Home",
      path: "/main/dashboard",
      icon: <HomeIcon />,
      roles: ["customer"],
    },
    {
      text: "Home",
      path: "/main/admin-dashboard",
      icon: <HomeIcon />,
      roles: ["admin"],
    },
    {
      text: "Site Estimator",
      path: "/main/generate-reports",
      icon: <ListAltIcon />,
      roles: ["customer"],
      condition: isSiteEstimator,
    },
    {
      text: "Fleet Estimator",
      path: "/main/fleet-reports",
      icon: <LibraryBooksIcon />,
      roles: ["customer"],
      condition: isFleetEstimator,
    },
    {
      text: "Estimator Hook",
      path: "/main/estimator-hook",
      icon: <BarChartIcon />,
      roles: ["customer"],
      condition: isEmbedded,
    },
    {
      text: "Add White Labels",
      path: "/main/add-white-labels",
      icon: <BookIcon />,
      roles: ["customer"],
      condition: isWhiteList,
    },
    {
      text: "Track Your Leads",
      path: "/main/track-your-leads",
      icon: <AutoGraphIcon />,
      roles: ["admin"],
    },
    {
      text: "Subscriptions",
      path: "/main/subscriptions",
      icon: <SubscriptionsIcon />,
      roles: ["admin"],
    },
    {
      text: "User",
      path: "/main/user-management",
      icon: <GroupIcon />,
      roles: ["admin"],
    },
    {
      text: "Past Reports",
      path: "/main/past-reports",
      icon: <EqualizerIcon />,
      roles: ["customer"],
    },
    {
      text: "Embed Reports",
      path: "/main/embed-reports",
      icon: <SummarizeIcon />,
      roles: ["customer"],
      condition: isEmbedded,
    },
    {
      text: "Plans",
      path: "/main/plans",
      icon: <MilitaryTechIcon />,
      roles: ["customer"],
    },
    {
      text: "Profile",
      path: "/main/user-profile",
      icon: <PersonIcon />,
      roles: ["admin", "customer"],
    },
    {
      text: "L3 Estimator",
      path: "/main/contact-us",
      icon: <ContactMailIcon />,
      roles: ["admin", "customer"],
    },
  ];

  // Filter navigation items based on user role and subscription conditions
  const navItems = allNavItems.filter((item) => {
    const roleMatch = item.roles.includes(userRole);
    if (!roleMatch) return false;

    // Check for specific conditions
    if (item.text === "Estimator Hook" && !isEmbedded) return false;
    if (item.text === "Add White Labels" && !isWhiteList) return false;
    if (item.text === "Embed Reports" && !isEmbedded) return false;
    if (item.text === "Site Estimator" && !isSiteEstimator) return false;
    if (item.text === "Fleet Estimator" && !isFleetEstimator) return false;

    // Additional check for "Add White Labels" based on plan name
    // if (item.text === "Add White Labels" && currentPlanName === "starter")
    //   return false;

    return true;
  });

  const drawerContent = (
    <DrawerContentContainer>
      <LogoContainer>
        <img
          src={CompanyLogo}
          alt="RightSite Logo"
          style={{
            height: isVerySmallScreen
              ? "20px"
              : isSmallScreen
              ? "24px"
              : "28px",
          }}
        />
      </LogoContainer>

      {navItems.length > 7 ? (
        <PerfectScrollbar
          style={{
            maxHeight: "calc(100vh - 200px)",
            overflowX: "hidden",
            padding: theme.spacing(1),
          }}
          options={{ suppressScrollX: true }}
        >
          <List sx={{ px: 1, py: 0.5, mt: 2 }}>
            {navItems.map((item) => (
              <ListItem key={item.text} disablePadding sx={{ my: 1 }}>
                <NavLinkStyled to={item.path}>
                  <ListItemButton>
                    <ListItemIcon
                      sx={{
                        minWidth: isSmallScreen ? 35 : 35,
                        color: "#dbdede",
                      }}
                    >
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText
                      primary={item.text}
                      primaryTypographyProps={{
                        fontWeight: 500,
                        fontSize: isVerySmallScreen
                          ? "0.875rem"
                          : isSmallScreen
                          ? "0.9rem"
                          : "0.75rem",
                      }}
                    />
                  </ListItemButton>
                </NavLinkStyled>
              </ListItem>
            ))}
          </List>
        </PerfectScrollbar>
      ) : (
        <List sx={{ px: 1, py: 0.5, mt: 2 }}>
          {navItems.map((item) => (
            <ListItem key={item.text} disablePadding sx={{ my: 1 }}>
              <NavLinkStyled to={item.path}>
                <ListItemButton>
                  <ListItemIcon
                    sx={{
                      minWidth: isSmallScreen ? 35 : 35,
                      color: "#dbdede",
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={item.text}
                    primaryTypographyProps={{
                      fontWeight: 500,
                      fontSize: isVerySmallScreen
                        ? "0.87rem"
                        : isSmallScreen
                        ? "0.9rem"
                        : "0.75rem",
                    }}
                  />
                </ListItemButton>
              </NavLinkStyled>
            </ListItem>
          ))}
        </List>
      )}

      {/* Bottom Section: Upgrade Plan and User Profile */}
      <Box sx={{ marginTop: "auto" }}>
        <Divider sx={{ my: 1, mx: 1, backgroundColor: "#F5F0E7" }} />

        {/* Upgrade Plan Section */}
        {userRole === "customer" && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              border: `1px solid #dbdede`,
              mx: 1,
            }}
          >
            <Box
              sx={{
                background: "rgba(172, 180, 188, 0.3)",
                padding: theme.spacing(0.5),
                width: "100%",
                textAlign: "center",
              }}
            >
              <Box sx={{ display: "flex", justifyContent: "center", mb: 1 }}>
                <AutoGraphIcon sx={{ color: "#F5F0E7", fontWeight: 600 }} />
                <Typography
                  variant="subtitle1"
                  sx={{
                    color: "#F5F0E7",
                    fontWeight: 600,
                    ml: 1,
                    fontSize: "12px",
                  }}
                >
                  Change Plan
                </Typography>
              </Box>

              <Typography
                variant="body2"
                sx={{ color: "#F5F0E7", fontSize: "0.65rem" }}
              >
                More access to best Models
              </Typography>
              <Button
                variant="contained"
                sx={{
                  fontSize: 11,
                  mt: 1,
                  background: "linear-gradient(to bottom, #F5F0E7, #CDC8BE)",
                  color: "#455354",
                  "&:hover": { background: "#d0d0d0" },
                  textTransform: "none",
                  fontWeight: 600,
                }}
                onClick={handleOpenUpgradeMenu}
                disabled={currentPlanName === "dcfc pro"}
              >
                {currentPlanName === "core"
                  ? "Upgrade to Growth"
                  : currentPlanName === "growth"
                  ? "Upgrade to DCFC Starter"
                  : "On DCFC Starter Plan"}
              </Button>
            </Box>
          </Box>
        )}

        {/* User Profile Section */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            mt: 1,
            padding: theme.spacing(0.5),
            mx: 1,
            background: "rgba(255, 255, 255, 0.2)",
            border: `1px solid #dbdede`,
            width: "calc(100% - 16px)",
            cursor: "pointer",
            "&:hover": {
              background: "rgba(255, 255, 255, 0.25)",
            },
          }}
          onClick={handleMenuClick}
        >
          <Avatar
            sx={{
              width: 28,
              height: 28,
              mr: 1,
              background: "#acb4bc",
              color: "#4b5b5d",
            }}
            src={profileImg || undefined}
            alt={`${userFirstname} ${userLastname}`}
          >
            {profileImg ? null : avatarInitial}
          </Avatar>
          <Typography
            variant="body2"
            sx={{ color: "#F5F0E7", fontSize: "0.8rem", fontWeight: 500 }}
          >
            {userFirstname} {userLastname}
          </Typography>
          <IconButton
            size="small"
            sx={{ ml: "auto", color: "#dbdede" }}
            onClick={handleMenuClick}
          >
            <KeyboardArrowUpIcon />
          </IconButton>
        </Box>
      </Box>
    </DrawerContentContainer>
  );

  return (
    <Box sx={{ display: "flex", minHeight: "100vh" }}>
      <CssBaseline />

      {/* Top AppBar for Mobile */}
      {isMobile && (
        <AppBarStyled position="fixed" sx={{ zIndex: theme.zIndex.drawer + 1 }}>
          <Toolbar>
            <IconButton
              color="inherit"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 1 }}
            >
              <MenuIcon />
            </IconButton>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <img
                src={CompanyLogo}
                alt="RightSite Logo"
                style={{
                  height: isVerySmallScreen
                    ? "20px"
                    : isSmallScreen
                    ? "24px"
                    : "28px",
                  marginRight: theme.spacing(1),
                }}
              />
            </Box>
          </Toolbar>
        </AppBarStyled>
      )}

      {/* Sidebar Drawer */}
      <DrawerStyled
        variant={isMobile ? "temporary" : "persistent"}
        open={open}
        onClose={handleDrawerToggle}
        ModalProps={{ keepMounted: true }}
      >
        {drawerContent}
      </DrawerStyled>

      {/* Main Content */}
      <Main open={!isMobile && open}>
        {isMobile && <Toolbar />}
        <ContentContainer>
          <Content>
            <PerfectScrollbar
              containerRef={(ref) => {
                scrollContainerRef.current = ref;
              }}
              options={{ suppressScrollX: true }}
              style={{
                maxHeight: "calc(100vh - 90px)",
                overflow: "hidden",
              }}
            >
              <Outlet />
            </PerfectScrollbar>
          </Content>
        </ContentContainer>
      </Main>

      {/* User menu dropdown */}
      <Menu
        anchorEl={anchorEl}
        open={menuOpen}
        onClose={handleMenuClose}
        onClick={handleMenuClose}
        PaperProps={{
          elevation: 1,
          sx: {
            overflow: "visible",
            filter: "drop-shadow(0px 4px 12px rgba(0,0,0,0.15))",
            mt: 2,
            height: "auto",
            minWidth: 120,
            borderRadius: 2,
            background: "linear-gradient(to bottom, #ffffff, #f8f9fa)",
            "&:before": {
              content: '""',
              display: "block",
              position: "absolute",
              top: 0,
              right: 14,
              width: 10,
              height: 10,
              bgcolor: "background.paper",
              transform: "translateY(-50%) rotate(45deg)",
              zIndex: 0,
            },
          },
        }}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
        TransitionComponent={Fade}
      >
        <MenuItem
          onClick={handleLogoutClick}
          sx={{
            py: 1,
            px: 2,
            "&:hover": {
              backgroundColor: "rgba(244, 67, 54, 0.08)",
            },
          }}
        >
          <ListItemIcon sx={{ color: "error.main", minWidth: 30 }}>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          <Typography variant="body2" color="error">
            Logout
          </Typography>
        </MenuItem>
      </Menu>

      {/* Upgrade duration menu */}
      <Menu
        anchorEl={upgradeAnchorEl}
        open={upgradeMenuOpen}
        onClose={handleCloseUpgradeMenu}
        PaperProps={{
          elevation: 3,
          sx: {
            mt: 2,
            minWidth: 180,
            background: "#F5F0E7",
            borderRadius: 2,
          },
        }}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      >
        <MenuItem onClick={() => handleSelectDurationAndOpenDialog("monthly")}>
          <ListItemIcon sx={{ minWidth: 36 }}>
            <CalendarMonthIcon fontSize="small" />
          </ListItemIcon>
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              Monthly
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Billed every month
            </Typography>
          </Box>
        </MenuItem>
        <MenuItem onClick={() => handleSelectDurationAndOpenDialog("yearly")}>
          <ListItemIcon sx={{ minWidth: 36 }}>
            <SubscriptionsIcon fontSize="small" />
          </ListItemIcon>
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              Yearly
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Best value, billed once a year
            </Typography>
          </Box>
        </MenuItem>
      </Menu>

      {/* Update Subscription Dialog */}
      <StyledDialog
        open={upgradeDialogOpen}
        onClose={handleCloseUpgradeDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogHeader>
          <DialogTitle
            sx={{ color: "#f2efe9", p: 0, m: 0, fontSize: isMobile ? 18 : 20 }}
          >
            Update Your Subscription
          </DialogTitle>
          <IconButton
            aria-label="close"
            onClick={handleCloseUpgradeDialog}
            sx={{
              position: "absolute",
              right: 12,
              top: 12,
              color: "#f2efe9",
            }}
          >
            <Close fontSize="small" />
          </IconButton>
        </DialogHeader>
        <DialogBody>
          {targetPlan && (
            <Elements stripe={stripePromise}>
              {/* <PaymentForm
                selectedPlan={targetPlan}
                onClose={handleCloseUpgradeDialog}
              /> */}
            </Elements>
          )}
        </DialogBody>
        <DialogFooter>
          <CancelButton onClick={handleCloseUpgradeDialog}>Cancel</CancelButton>
          <StyledButton
            type="submit"
            form="payment-form"
            variant="contained"
            disabled={updateSubscriptionLoading}
          >
            {updateSubscriptionLoading ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CircularProgress
                  size={isMobile ? 20 : 24}
                  sx={{ color: "#fff" }}
                />
                <Typography variant="button">Processing...</Typography>
              </Box>
            ) : (
              "Update Subscription"
            )}
          </StyledButton>
        </DialogFooter>
      </StyledDialog>

      {/* Enhanced Logout Confirmation Dialog */}
      <GradientDialog
        open={logoutDialogOpen}
        onClose={handleLogoutCancel}
        aria-labelledby="logout-dialog-title"
      >
        <DialogTitleStyled id="logout-dialog-title">
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              mr: 2,
            }}
          >
            <LogoutIcon sx={{ color: "#F5F0E7", fontSize: 25, opacity: 0.8 }} />
          </Box>
          Confirm Logout
        </DialogTitleStyled>
        <DialogContentStyled>
          <DialogContentText sx={{ color: "#F5F0E7" }}>
            Are you sure you want to logout from your account?
          </DialogContentText>
        </DialogContentStyled>
        <DialogActionsStyled>
          <GradientButton
            onClick={handleLogoutCancel}
            className="cancel-button"
          >
            Cancel
          </GradientButton>
          <GradientButton
            onClick={handleLogoutConfirm}
            className="logout-button"
            autoFocus
          >
            Logout
          </GradientButton>
        </DialogActionsStyled>
      </GradientDialog>
    </Box>
  );
};

export default Layout;
